import axios from "axios";
const API_URL = process.env.REACT_APP_API_URL;

export const getNotifications = () => {
  return axios.get(`${API_URL}notifications`);
};

export const downloadInvoice = async (invoiceNumber) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Customer token not found");

  const response = await axios.get(
    `${process.env.REACT_APP_API_URL}invoices/${invoiceNumber}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: "blob",
    }
  );

  return response.data;
};

export const createTicket = (data) => {
  const token = localStorage.getItem("token");
  const bigcommerceCustomerId = localStorage.getItem("customerId");

  return axios.post(`${API_URL}support-tickets/tickets`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      customer_id: bigcommerceCustomerId,
    },
  });
};

export const getCustomerProfile = () => {
  const token = localStorage.getItem("token");
  return axios.get(`${API_URL}customers/profile`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const fetchCustomerDetails = (token) => {
  return axios.get(`${API_URL}customers/details`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const getCustomerAddresses = () => {
  const token = localStorage.getItem("token");
  return axios.get(`${API_URL}customer-address/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const createAddress = (address) => {
  const token = localStorage.getItem("token");
  return axios.post(`${API_URL}customer-address`, address, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
};

export const updateAddress = (id, payload) => {
  const token = localStorage.getItem("token");
  return axios.put(`${API_URL}customer-address/${id}`, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
};

// Delete address
export const deleteAddress = (id) => {
  const token = localStorage.getItem("token");
  return axios.delete(`${API_URL}customer-address/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
};

// ----- Customer Payment Endpoints -----
export const customerPaymentOrBooking = (paymentData) => {
  const token = localStorage.getItem("token");
  return axios.post(`${API_URL}customer-payments`, paymentData, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};

// Verify Payment
export const customerVerifyPayment = (cartId) => {
  const token = localStorage.getItem("token");
  return axios.get(`${API_URL}customer-payments/verify`, {
    params: { cart_id: cartId },
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};

export const confirmInStorePayment = async (bookings, recaptchaToken, couponCode) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Customer token not found");

  return axios.post(
    `${API_URL}store-payment/confirm-instore-payment`,
    {
      bookings,
      recaptchaToken,
      coupon_code: couponCode,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
};

export const updateCustomerPhone = (phone) => {
  const token = localStorage.getItem("token");
  return axios.patch(
    `${API_URL}customers/profile/phone`,
    { phone },
    { headers: { Authorization: `Bearer ${token}` } }
  );
};

export const getRacketFreeEligibility = async (rpId) => {
  const token = localStorage.getItem("token");
  const customerId = localStorage.getItem("customerId");
  if (!customerId || !rpId) throw new Error("customerId and rpId are required");

  const { data } = await axios.get(
    `${API_URL}free-service/eligibility/racket`,
    {
      params: { customerId, rpId },
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  return data;
};

export const getLiveGrips = async () => {
  const token = localStorage.getItem("token");
  const res = await axios.get(`${API_URL}grips/live`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    params: { _ts: Date.now() },
  });

  if (!res?.data) throw new Error("No data from grips/live");
  return Array.isArray(res.data.data) ? res.data.data : res.data;
};

export const confirmInStorePaymentGrip = async (bookings, recaptchaToken) => {
  const token = localStorage.getItem("token");
  const url = `${API_URL}/confirm-instore-payment/grip`;
  const res = await axios.post(
    url,
    { bookings, recaptchaToken },
    {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "x-recaptcha-token": recaptchaToken,
        "Content-Type": "application/json",
      },
      params: { _ts: Date.now() },
    }
  );

  if (!res?.data) throw new Error("No response from confirmInStorePaymentGrip");
  return res.data;
};

// Create Grip Payment or Booking
export const customerPaymentOrBookingGrip = (paymentData) => {
  const token = localStorage.getItem("token");
  return axios.post(`${API_URL}grip-payments`, paymentData, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};

// Verify Grip Payment
export const customerVerifyGripPayment = (cartId) => {
  const token = localStorage.getItem("token");
  return axios.get(`${API_URL}grip-payments/verify`, {
    params: { cart_id: cartId },
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};

// ----- String Inventory Endpoints -----
export const getAllStringsFromInventory = async () => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Customer token not found");
  
  const response = await axios.get(`${API_URL}string-inventory`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  
  return response.data;
};

export const getStringById = async (stringId) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Customer token not found");
  
  const response = await axios.get(`${API_URL}string-inventory/${stringId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  
  return response.data;
};

export const checkStringStockAtLocation = async (stringId, location) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Customer token not found");
  
  const response = await axios.get(
    `${API_URL}string-inventory/${stringId}/stock/${location}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  
  return response.data;
};

export const getActiveDiscountApi = () => {
  return axios.get(`${API_URL}discounts/active`);
};

// WhatsApp string booking checkout
export const createWhatsappStringBooking = (bookingData) => {
  return axios.post(`${API_URL}customer-payments/whatsapp`, bookingData, {
    headers: { "Content-Type": "application/json" },
  });
};

// WhatsApp grip booking checkout
export const createWhatsappGripBooking = (bookingData) => {
  return axios.post(`${API_URL}grip-payments/whatsapp`, bookingData, {
    headers: { "Content-Type": "application/json" },
  });
};

// Get public strings
export const getPublicStrings = () => {
  return axios.get(`${API_URL}string-inventory/public`);
};

// Express booking settings
export const getExpressSettingsApi = () => {
  return axios.get(`${API_URL}express-settings`);
};
